/**
 * Usage months (SPEC §18.1, decision 2026-10-22): a usage record belongs to the calendar month, in UTC, in which its
 * build reached a terminal state (`YYYY-MM`). A closed month therefore never changes, which is what an invoice export
 * needs. Pure helpers shared by the worker (which writes the month), the API (which filters by it) and the console
 * (month picker); nothing here reads the clock unless the caller passes it.
 */
import { z } from 'zod';

/** `YYYY-MM`, January to December. */
export const UsageMonth = z.string().regex(/^[0-9]{4}-(0[1-9]|1[0-2])$/, 'a month as YYYY-MM');
export type UsageMonth = z.infer<typeof UsageMonth>;

export const isUsageMonth = (value: unknown): value is UsageMonth => UsageMonth.safeParse(value).success;

const pad = (n: number): string => String(n).padStart(2, '0');

/** The UTC month of an instant: `2026-10-31T23:59:59.999Z` is `2026-10`, one millisecond later is `2026-11`. */
export function monthOf(at: Date): UsageMonth {
  return `${at.getUTCFullYear()}-${pad(at.getUTCMonth() + 1)}`;
}

function partsOf(month: UsageMonth): { year: number; index: number } {
  return { year: Number(month.slice(0, 4)), index: Number(month.slice(5, 7)) - 1 };
}

/** First instant of the month (inclusive), UTC. */
export function monthStart(month: UsageMonth): Date {
  const { year, index } = partsOf(month);
  return new Date(Date.UTC(year, index, 1));
}

/** First instant of the next month (exclusive end of `month`), UTC. */
export function nextMonthStart(month: UsageMonth): Date {
  const { year, index } = partsOf(month);
  return new Date(Date.UTC(year, index + 1, 1));
}

/** `shiftMonth('2026-01', -1)` is `2025-12`. */
export function shiftMonth(month: UsageMonth, delta: number): UsageMonth {
  const { year, index } = partsOf(month);
  return monthOf(new Date(Date.UTC(year, index + delta, 1)));
}

/** The last `count` months ending at `month` (inclusive), newest first. */
export function monthsBack(month: UsageMonth, count: number): UsageMonth[] {
  return Array.from({ length: Math.max(0, count) }, (_, i) => shiftMonth(month, -i));
}
